// frontend/src/components/Layout/AssetAdminLayout.js
/**
 * Asset Admin Layout Component
 * 
 * A specialized layout for the Asset Inventory admin portal.
 * Uses Red (Admin) theme per design system.
 * 
 * Features:
 * - Sidebar navigation for asset management
 * - Subdomain detection with localhost fallback
 * - Only accessible to users with is_asset_custodian permission
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import toast from '../../utils/toast';
import { useAuth } from '../../contexts/AuthContext';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useBreakpointBoundRef } from '../../hooks/useBreakpointBoundRef';
import { useScrollMainToTop } from '../../hooks/useScrollMainToTop';
import { useMobileSidebarOpen } from '../../hooks/useMobileSidebarOpen';
import { useSidebarCollapsed } from '../../hooks/useSidebarCollapsed';
import { useViewportAnchoredPanel, USER_MENU_PANEL_OPTIONS, getViewportPanelStyle } from '../../hooks/useViewportAnchoredPanel';
import { useTailwindLg } from '../../hooks/useTailwindLg';
import { authAPI } from '../../services/api';
import PullToRefresh from '../UI/PullToRefresh';
import PasswordChangeModal from '../UI/PasswordChangeModal';
import { CountBadge } from '../UI/CountBadge';
import {
  LayoutDashboard,
  Package,
  ArrowLeftRight,
  ClipboardCheck,
  Trash2,
  Menu,
  X,
  Bell,
  User,
  LogOut,
  ChevronDown,
  Home,
  Laptop,
  Key,
  Car,
  FileKey,
  Settings,
  Users,
  AlertTriangle,
  RotateCcw,
  QrCode,
  Shield,
  ShieldCheck,
  Wrench,
  Globe,
  ListChecks,
  ShieldAlert,
  Siren,
} from 'lucide-react';
import { usePendingReturnsForCustodian } from '../../hooks/useAssetInventoryData';
import { useAssetDetail } from '../../contexts/AssetDetailContext';
import AssetQRScannerModal from '../AssetQRScannerModal';
import { getClassificationAvatarHex } from '../../utils/classificationConfig';
import weplLogo from '../../assets/images/wepl_logo.png';

/**
 * Detect if we're on the Asset Admin portal.
 * Handles: assets.wepl.site, localhost, IP addresses
 */
export const isAssetPortal = () => {
  const hostname = window.location.hostname;
  
  // Production: subdomain-based
  if (hostname.startsWith('assets.')) {
    return true;
  }
  
  // Development fallback: path-based detection
  if (hostname === 'localhost' || hostname.startsWith('192.168') || hostname.startsWith('127.')) {
    // Use URL param or path for local dev
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('portal') === 'assets' || 
           window.location.pathname.startsWith('/asset-admin');
  }
  
  return false;
};

const AssetAdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { sidebarOpen, setSidebarOpen, onOverlayClick, menuButtonProps } = useMobileSidebarOpen();
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarCollapsed(user?.staff_id || user?.id);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState(null);
  const userMenuRef = useClickOutside(() => setUserMenuOpen(false), userMenuOpen);
  const isLg = useTailwindLg();
  const {
    bindMobile: bindMobileUserMenuRef,
    bindDesktop: bindDesktopUserMenuRef,
    visibleRef: userMenuViewportAnchorRef,
  } = useBreakpointBoundRef(isLg, userMenuRef);
  const { panelRect: userMenuPanelRect } = useViewportAnchoredPanel({
    open: userMenuOpen,
    anchorRef: userMenuViewportAnchorRef,
    ...USER_MENU_PANEL_OPTIONS,
  });
  const mainScrollRef = useRef(null);
  const scrollMainToTop = useScrollMainToTop(mainScrollRef);
  const [scannerOpen, setScannerOpen] = useState(false);

  const { openAssetDetail } = useAssetDetail();

  // Fetch pending returns count for badge
  const { data: pendingReturns = [] } = usePendingReturnsForCustodian();
  const pendingReturnsCount = pendingReturns.length;

  // Close scanner when route changes
  useEffect(() => {
    setScannerOpen(false);
  }, [location.pathname]);


  // Handle voluntary password change
  const handlePasswordChange = async (currentPassword, newPassword) => {
    setPasswordChangeLoading(true);
    setPasswordChangeError(null);
    try {
      const response = await authAPI.changePassword(currentPassword, newPassword);
      if (response?.data?.success || response?.status === 200) {
        setPasswordModalOpen(false);
        toast.success('Password changed successfully! You have been logged out of other devices.');
      } else {
        setPasswordChangeError('Failed to change password. Please try again.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to change password. Please try again.';
      setPasswordChangeError(errorMessage);
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  // Redirect if not an asset custodian — use <Navigate> not imperative navigate() during render
  if (!user?.is_asset_custodian) {
    return <Navigate to="/dashboard" replace />;
  }

  const assetNavigation = [
    {
      name: 'Dashboard',
      href: '/asset-admin',
      icon: LayoutDashboard,
      description: 'Overview & Statistics'
    },
    {
      name: 'Scan',
      type: 'action',
      icon: QrCode,
      description: 'Identify asset by QR code',
      action: () => setScannerOpen(true)
    },
    {
      name: 'Assets',
      href: '/asset-admin/assets',
      icon: Package,
      description: 'Manage All Assets'
    },
    {
      name: 'Transactions',
      href: '/asset-admin/transfers',
      icon: ArrowLeftRight,
      description: 'Movement history & approvals'
    },
    {
      name: 'Pending Returns',
      href: '/asset-admin/pending-returns',
      icon: RotateCcw,
      description: 'Review Return Requests',
      badge: pendingReturnsCount
    },
    {
      name: 'Checklists',
      href: '/asset-admin/checklists',
      icon: ClipboardCheck,
      description: 'Onboarding & Offboarding'
    },
    {
      name: 'Disposal Requests',
      href: '/asset-admin/disposal',
      icon: Trash2,
      description: 'Review & Approve Disposals'
    },
    {
      name: 'Endpoint Security',
      href: '/asset-admin/endpoint-security',
      icon: ShieldCheck,
      description: 'Trend Micro coverage & licensing'
    },
    {
      name: 'Fleet Maintenance',
      href: '/asset-admin/fleet-maintenance',
      icon: Wrench,
      description: 'Service history & parts demand'
    },
    {
      name: 'Fleet Inspections',
      href: '/asset-admin/fleet-inspections',
      icon: ListChecks,
      description: 'Monthly checklist & compliance calendar'
    },
    {
      name: 'Vehicle Accidents',
      href: '/asset-admin/fleet-accidents',
      icon: Siren,
      description: 'Insurer clock, SAS & claims'
    },
    {
      name: 'Traffic Offences',
      href: '/asset-admin/fleet-offences',
      icon: ShieldAlert,
      description: 'Driver declarations & fine tracking'
    },
    {
      name: 'Configuration',
      href: '/asset-admin/categories',
      icon: Settings,
      description: 'Categories, Types & Vendors'
    },
    {
      name: 'External Personnel',
      href: '/asset-admin/external-personnel',
      icon: Globe,
      description: 'Outsourced asset holders (JB office)'
    },
    {
      name: 'API Access',
      href: '/asset-admin/api-access',
      icon: Key,
      description: 'Service API keys for asset & fleet data'
    },
    // Only HR admins (has_admin_access) should manage who is a custodian
    ...(user?.has_admin_access ? [{
      name: 'Custodians',
      href: '/asset-admin/custodians',
      icon: Shield,
      description: 'Manage Portal Access'
    }] : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isCurrentPath = (path) => {
    if (path === '/asset-admin') {
      return location.pathname === '/asset-admin';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className="h-dvh bg-gray-50 flex overflow-hidden">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onOverlayClick} />
          <div className="fixed top-0 left-0 w-80 h-full bg-white shadow-xl">
            <AssetSidebarContent 
              navigation={assetNavigation} 
              isCurrentPath={isCurrentPath}
              user={user}
              onLogout={handleLogout}
              onClose={() => setSidebarOpen(false)}
              collapsed={false}
            />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className={`hidden lg:flex ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-72'} lg:flex-col lg:flex-shrink-0 transition-all duration-300`}>
        <AssetSidebarContent 
          navigation={assetNavigation} 
          isCurrentPath={isCurrentPath}
          user={user}
          onLogout={handleLogout}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile header — fixed inside the locked shell. paddingTop respects the
            iOS notch / Android cutout in PWA standalone mode. touchAction:
            'manipulation' blocks pan gestures so the page can never be dragged
            from the top bar. Empty-area tap → scroll <main> to top (mobile-only
            DS standard — useScrollMainToTop / App Shell). */}
        <div
          onClick={scrollMainToTop}
          className="lg:hidden bg-white shadow-sm border-b px-4 flex items-center justify-between flex-shrink-0"
          style={{
            paddingTop: 'env(safe-area-inset-top)',
            height: 'calc(4rem + env(safe-area-inset-top))',
            touchAction: 'manipulation',
          }}
        >
          <div className="flex items-center space-x-2 shrink-0">
            <button
              {...menuButtonProps}
              className="icon-button-admin"
            >
              <Menu className="w-6 h-6" />
            </button>
            <Link to="/" className="flex items-center shrink-0" aria-label="Go to dashboard">
              <img src={weplLogo} alt="Company Logo" className="h-7 w-auto sm:h-8" />
            </Link>
          </div>
          {/* Title removed - exists only in sidebar */}
          
          {/* Mobile User Profile */}
          <div className="flex items-center space-x-2">
            {/* User Menu */}
            <div className="relative shrink-0" ref={bindMobileUserMenuRef}>
              <button
                type="button"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-2 p-1 rounded-full hover:bg-gray-100 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                aria-expanded={userMenuOpen}
                aria-haspopup="menu"
              >
                <div 
                  className="w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                  style={{ 
                    backgroundColor: getClassificationAvatarHex(user?.employment_classification)
                  }}
                >
                  <span className="text-xs font-semibold text-white">
                    {user?.full_name?.charAt(0) || 'U'}
                  </span>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {/* User Dropdown — mount only on the visible header so click-outside
                  cannot tear the menu down before Sign out / row actions fire. */}
              {userMenuOpen && !isLg && (
                <div
                  className="fixed z-[70] rounded-2xl border border-gray-200 bg-white py-2 shadow-lg"
                  style={getViewportPanelStyle(userMenuPanelRect)}
                  role="menu"
                >
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-700">
                      {user?.full_name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 font-mono">
                      {user?.staff_id || '--'}
                    </p>
                  </div>
                  <div className="px-2 space-y-0.5">
                    <Link
                      to="/dashboard"
                      role="menuitem"
                      className="flex items-center w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Home className="w-4 h-4 mr-3 shrink-0" />
                      HR Portal
                    </Link>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setUserMenuOpen(false);
                        setPasswordModalOpen(true);
                      }}
                      className="flex items-center w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                    >
                      <Key className="w-4 h-4 mr-3 shrink-0" />
                      Change Password
                    </button>
                  </div>
                  <div className="border-t border-gray-100 my-2 mx-3" />
                  <div className="px-2 pb-1">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={(e) => {
                        e.stopPropagation();
                        setUserMenuOpen(false);
                        handleLogout();
                      }}
                      className="flex items-center w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                    >
                      <LogOut className="w-4 h-4 mr-3 shrink-0" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Desktop header — scroll-to-top is mobile-only (see useScrollMainToTop). */}
        <div
          className="hidden lg:flex bg-white border-b px-4 lg:px-6 h-16 items-center justify-between flex-shrink-0"
        >
          {/* Left Section: Logo only - Title exists only in sidebar */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center shrink-0" aria-label="Go to dashboard">
              <img src={weplLogo} alt="Company logo" className="h-7 w-auto" />
            </Link>
          </div>

          {/* Right Section: User Profile */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* User Profile */}
            <div className="relative shrink-0" ref={bindDesktopUserMenuRef}>
              <button
                type="button"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-3 p-2 rounded-full hover:bg-gray-100 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                aria-expanded={userMenuOpen}
                aria-haspopup="menu"
              >
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center shadow-sm"
                  style={{ 
                    backgroundColor: getClassificationAvatarHex(user?.employment_classification)
                  }}
                >
                  <span className="text-sm font-semibold text-white">
                    {user?.full_name?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-700">
                    {user?.full_name || 'User'}
                  </p>
                  <p className="text-xs text-gray-500">Asset Custodian</p>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {/* User Dropdown — desktop copy only while lg is visible. */}
              {userMenuOpen && isLg && (
                <div
                  className="fixed z-[70] rounded-2xl border border-gray-200 bg-white py-2 shadow-lg"
                  style={getViewportPanelStyle(userMenuPanelRect)}
                  role="menu"
                >
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-700">
                      {user?.full_name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 font-mono">
                      {user?.staff_id || '--'}
                    </p>
                    {user?.work_email && (
                      <p className="text-xs text-gray-400 mt-1 break-all">
                        {user.work_email}
                      </p>
                    )}
                  </div>
                  
                  {/* Switch to HR Portal */}
                  <div className="px-3 py-2">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 px-1">Switch Portal</p>
                    <Link
                      to="/dashboard"
                      role="menuitem"
                      className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded-lg transition-all duration-300 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Users className="w-4 h-4 mr-3 shrink-0 text-gray-400 group-hover:text-blue-600" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">HR Portal</p>
                        <p className="text-xs text-gray-500">Employee dashboard</p>
                      </div>
                    </Link>
                  </div>
                  
                  <div className="border-t border-gray-100 my-2 mx-3" />
                  
                  <div className="px-2 space-y-0.5">
                    <Link
                      to="/profile"
                      role="menuitem"
                      className="flex items-center w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <User className="w-4 h-4 mr-3 shrink-0" />
                      My Profile
                    </Link>
                    
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setUserMenuOpen(false);
                        setPasswordModalOpen(true);
                      }}
                      className="flex items-center w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                    >
                      <Key className="w-4 h-4 mr-3 shrink-0" />
                      Change Password
                    </button>
                  </div>
                  
                  <div className="border-t border-gray-100 my-2 mx-3" />
                  <div className="px-2 pb-1">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={(e) => {
                        e.stopPropagation();
                        setUserMenuOpen(false);
                        handleLogout();
                      }}
                      className="flex items-center w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                    >
                      <LogOut className="w-4 h-4 mr-3 shrink-0" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Page content - Scrollable */}
        <main ref={mainScrollRef} className="flex-1 overflow-y-auto overflow-x-hidden bg-gray-50 p-4 md:p-6 pb-safe" style={{ touchAction: 'pan-y' }}>
          <PullToRefresh scrollContainerRef={mainScrollRef}>
            {children}
          </PullToRefresh>
        </main>

        {/* QR Scanner Modal (standalone from sidebar) */}
        <AssetQRScannerModal
          isOpen={scannerOpen}
          onClose={() => setScannerOpen(false)}
          onScanSuccess={(asset) => {
            openAssetDetail(undefined, asset);
            setScannerOpen(false);
          }}
        />

        {/* Password Change Modal */}
        <PasswordChangeModal
          isOpen={passwordModalOpen}
          onChangePassword={handlePasswordChange}
          onClose={() => {
            setPasswordModalOpen(false);
            setPasswordChangeError(null);
          }}
          loading={passwordChangeLoading}
          error={passwordChangeError}
          mode="voluntary"
        />
      </div>
    </div>
  );
};

const AssetSidebarContent = ({ navigation, isCurrentPath, user, onLogout, onClose, collapsed, onToggleCollapse }) => {
  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-200">
      {/* Header - Fixed */}
      <div className={`flex items-center ${collapsed ? 'justify-center px-2' : 'justify-between px-6'} h-16 border-b border-gray-200 flex-shrink-0`}>
        {collapsed ? (
          /* Collapsed state - Only show hamburger menu */
          <button 
            onClick={onToggleCollapse} 
            className="icon-button-admin"
            title="Expand sidebar"
          >
            <Menu className="w-6 h-6" />
          </button>
        ) : (
          /* Expanded state - Show logo, text, and controls */
          <>
            <div className="flex items-center space-x-3">
              <img 
                src={weplLogo} 
                alt="Company Logo" 
                className="h-7 w-auto object-contain"
              />
              <div>
                <span className="text-xl font-bold text-gray-900">Asset Portal</span>
                <p className="text-xs text-gray-500 font-medium">Management Portal</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {onToggleCollapse && (
                <button 
                  onClick={onToggleCollapse} 
                  className="icon-button-admin hidden lg:inline-flex"
                  title="Collapse sidebar"
                >
                  <Menu className="w-5 h-5" />
                </button>
              )}
              {onClose && (
                <button onClick={onClose} className="icon-button-admin lg:hidden">
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Navigation - Scrollable */}
      <nav className={`flex-1 ${collapsed ? 'px-2' : 'px-4'} py-4 space-y-2 overflow-y-auto`}>
        {navigation.map((item) => {
          const isAction = item.type === 'action' && item.action;
          const isActive = !isAction && isCurrentPath(item.href);
          const baseClass = `flex items-center rounded-full transition-all duration-300 group relative text-left ${
            isActive
              ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg hover:shadow-xl'
              : 'text-gray-700 hover:bg-red-50 hover:text-red-700'
          } ${collapsed ? 'justify-center w-11 h-11 mx-auto' : 'w-full px-4 py-3'}`;

          const content = (
            <>
              <item.icon
                className={`w-5 h-5 ${collapsed ? '' : 'mr-3'} flex-shrink-0 ${
                  isActive ? 'text-white' : 'text-gray-400 group-hover:text-red-600'
                } transition-colors duration-300`}
              />
              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${isActive ? 'text-white' : 'text-gray-900'} transition-colors duration-300`}>
                    {item.name}
                  </p>
                  <p className={`text-xs mt-0.5 ${
                    isActive ? 'text-red-100' : 'text-gray-500'
                  } transition-colors duration-300`}>
                    {item.description}
                  </p>
                </div>
              )}
              {item.badge > 0 && (
                <CountBadge
                  count={item.badge}
                  theme={isActive ? 'admin' : 'orange'}
                  active={isActive}
                  overlay={collapsed}
                  overlayPosition={collapsed ? 'inside' : 'outside'}
                  className={collapsed ? '' : 'ml-2'}
                />
              )}
            </>
          );

          if (isAction) {
            return (
              <button
                key={item.name}
                type="button"
                onClick={() => {
                  item.action();
                  onClose?.();
                }}
                className={baseClass}
                title={collapsed ? item.name : undefined}
              >
                {content}
              </button>
            );
          }
          return (
            <Link key={item.name} to={item.href} onClick={onClose} className={baseClass} title={collapsed ? item.name : undefined}>
              {content}
            </Link>
          );
        })}
      </nav>

      {/* Back to HR Portal - Footer */}
      <div className={`${collapsed ? 'px-2' : 'px-4'} py-4 border-t border-gray-200`}>
        <Link
          to="/dashboard"
          onClick={onClose}
          className={`flex items-center rounded-full transition-all duration-300 group ${
            collapsed ? 'justify-center w-11 h-11 mx-auto' : 'w-full px-4 py-3'
          } bg-gradient-to-r from-blue-900 to-blue-800 text-white shadow-lg hover:shadow-xl`}
          title={collapsed ? 'Back to HR Portal' : undefined}
        >
          <Home
            className={`w-5 h-5 ${collapsed ? '' : 'mr-3'} flex-shrink-0 text-white transition-colors duration-300`}
          />
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white transition-colors duration-300">
                HR Portal
              </p>
              <p className="text-xs mt-0.5 text-blue-100 transition-colors duration-300">
                Employee Dashboard
              </p>
            </div>
          )}
        </Link>
      </div>

    </div>
  );
};

export default AssetAdminLayout;
